/**
 * Kontrollert CSV-import av demonstrasjonsfiler (maskinintervaller).
 * Kolonner: record_id;machine_id;from_utc;to_utc;engine_hours;idle_hours;fuel_litres;quality[;revision][;reported_co2_kg][;activity][;source][;hours_unit][;fuel_unit]
 * Se importContract.ts (VISIONLINK_CONTRACT) for full kontrakt med enheter, tidssone, identitet og duplikatregel.
 * Hele filen avvises ved feil, uten delvis mutasjon. Idempotent: samme record_id + revision gir ingen endring.
 */
import Papa from 'papaparse';
import type { Dataset, MachineInterval, Quality } from '../domain/types';
import { hoursBetween, isIsoUtc } from '../domain/time';

export const CSV_MAX_BYTES = 5 * 1024 * 1024;
export const CSV_MAX_ROWS = 10000;
export const REQUIRED_COLUMNS = ['record_id', 'machine_id', 'from_utc', 'to_utc', 'engine_hours', 'idle_hours', 'fuel_litres', 'quality'] as const;
export const OPTIONAL_COLUMNS = ['revision', 'reported_co2_kg', 'activity', 'source', 'hours_unit', 'fuel_unit'] as const;
const ACTIVITIES = ['production', 'loading_out', 'service'] as const;
const SOURCES = ['visionlink', 'manual', 'other'] as const;
const QUALITIES: Quality[] = ['measured', 'estimated', 'missing', 'imported'];

export interface RowError {
  row: number;
  column: string;
  value: string;
  reason: string;
}

export interface ParsedRow {
  row: number;
  recordId: string;
  machineId: string;
  fromUtc: string;
  toUtc: string;
  engineHours: number | null;
  idleHours: number | null;
  fuelLitres: number | null;
  /** CO₂ rapportert direkte av telematikk (kg). null = ikke rapportert → estimat fra diesel. */
  reportedCo2Kg: number | null;
  quality: Quality;
  revision: number;
  activity?: string;
  sourceSystem?: string;
}

export interface CsvParseResult {
  ok: boolean;
  rows: ParsedRow[];
  errors: RowError[];
  headerErrors: string[];
  rowCount: number;
  delimiter: string;
}

function parseNumber(raw: string): { value: number | null; error?: string } {
  const s = raw.trim();
  if (s === '') return { value: null };
  if (/[a-zA-Z]/.test(s)) return { value: null, error: 'Verdien inneholder bokstaver/enhet. Oppgi tall uten enhet.' };
  const normalized = s.replace(/\s/g, '').replace(',', '.');
  const v = Number(normalized);
  if (!Number.isFinite(v)) return { value: null, error: 'Ikke et gyldig tall.' };
  return { value: v };
}

export function parseMachineCsv(text: string, knownMachineIds: string[]): CsvParseResult {
  const clean = text.replace(/^\uFEFF/, '');
  const parsed = Papa.parse<Record<string, string>>(clean, { header: true, skipEmptyLines: 'greedy', delimiter: '', transformHeader: (h) => h.trim().toLowerCase() });
  const headerErrors: string[] = [];
  const fields = parsed.meta.fields ?? [];
  for (const c of REQUIRED_COLUMNS) if (!fields.includes(c)) headerErrors.push(`Mangler obligatorisk kolonne «${c}»`);
  for (const f of fields) {
    if (!(REQUIRED_COLUMNS as readonly string[]).includes(f) && !(OPTIONAL_COLUMNS as readonly string[]).includes(f)) headerErrors.push(`Ukjent kolonne «${f}»`);
  }
  const errors: RowError[] = [];
  const rows: ParsedRow[] = [];
  const data = parsed.data;
  if (data.length > CSV_MAX_ROWS) {
    headerErrors.push(`Filen har ${data.length} rader; maks ${CSV_MAX_ROWS} rader er tillatt.`);
  }
  if (headerErrors.length) {
    return { ok: false, rows: [], errors, headerErrors, rowCount: data.length, delimiter: parsed.meta.delimiter };
  }
  const seen = new Map<string, number>();
  data.forEach((r, idx) => {
    const rowNo = idx + 2; // 1 = header
    const err = (column: string, value: string, reason: string) => errors.push({ row: rowNo, column, value, reason });
    const recordId = (r.record_id ?? '').trim();
    if (!recordId) err('record_id', r.record_id ?? '', 'Tom record_id');
    const rawMachineId = (r.machine_id ?? '').trim();
    // Visningsformen uten «DEMO-»-prefiks godtas og normaliseres til den underliggende ID-en.
    const machineId = knownMachineIds.includes(rawMachineId) ? rawMachineId : knownMachineIds.find((k) => k === `DEMO-${rawMachineId}`) ?? rawMachineId;
    if (!knownMachineIds.includes(machineId)) err('machine_id', machineId, 'Ukjent maskin-ID (må være en registrert maskin)');
    const fromUtc = (r.from_utc ?? '').trim();
    const toUtc = (r.to_utc ?? '').trim();
    if (!isIsoUtc(fromUtc)) err('from_utc', fromUtc, 'Må være ISO 8601 i UTC med Z (f.eks. 2026-10-02T07:00:00Z)');
    if (!isIsoUtc(toUtc)) err('to_utc', toUtc, 'Må være ISO 8601 i UTC med Z');
    let len = 0;
    if (isIsoUtc(fromUtc) && isIsoUtc(toUtc)) {
      len = hoursBetween(fromUtc, toUtc);
      if (!(len > 0)) err('to_utc', toUtc, 'to_utc må være etter from_utc (halvåpent intervall)');
    }
    const e = parseNumber(r.engine_hours ?? '');
    if (e.error) err('engine_hours', r.engine_hours, e.error);
    const i = parseNumber(r.idle_hours ?? '');
    if (i.error) err('idle_hours', r.idle_hours, i.error);
    const f = parseNumber(r.fuel_litres ?? '');
    if (f.error) err('fuel_litres', r.fuel_litres, f.error);
    if (e.value !== null && e.value < 0) err('engine_hours', r.engine_hours, 'Negativ verdi er ikke tillatt');
    if (i.value !== null && i.value < 0) err('idle_hours', r.idle_hours, 'Negativ verdi er ikke tillatt');
    if (f.value !== null && f.value < 0) err('fuel_litres', r.fuel_litres, 'Negativ verdi er ikke tillatt');
    if (e.value !== null && i.value !== null && i.value > e.value + 1e-9) err('idle_hours', r.idle_hours, 'Tomgangstimer kan ikke overstige motortimer');
    if (e.value !== null && len > 0 && e.value > len + 1e-9) err('engine_hours', r.engine_hours, `Motortimer (${e.value}) overstiger intervallets lengde (${len} h)`);
    const quality = (r.quality ?? '').trim() as Quality;
    if (!QUALITIES.includes(quality)) err('quality', r.quality ?? '', `Må være en av: ${QUALITIES.join(', ')}`);
    if (quality === 'missing' && f.value !== null && e.value !== null && i.value !== null) err('quality', quality, '«missing» krever minst ett tomt målefelt');
    const c = parseNumber(r.reported_co2_kg ?? '');
    if (c.error) err('reported_co2_kg', r.reported_co2_kg, c.error);
    if (c.value !== null && c.value < 0) err('reported_co2_kg', r.reported_co2_kg, 'Negativ verdi er ikke tillatt');
    let activity: string | undefined;
    if (r.activity !== undefined && r.activity.trim() !== '') {
      activity = r.activity.trim();
      if (!(ACTIVITIES as readonly string[]).includes(activity)) err('activity', r.activity, `Må være en av: ${ACTIVITIES.join(', ')}`);
    }
    let sourceSystem: string | undefined;
    if (r.source !== undefined && r.source.trim() !== '') {
      sourceSystem = r.source.trim().toLowerCase();
      if (!(SOURCES as readonly string[]).includes(sourceSystem)) err('source', r.source, `Må være en av: ${SOURCES.join(', ')}`);
    }
    if (r.hours_unit !== undefined && r.hours_unit.trim() !== '' && r.hours_unit.trim() !== 'h') err('hours_unit', r.hours_unit, 'Feil enhet: timer må oppgis i «h»');
    if (r.fuel_unit !== undefined && r.fuel_unit.trim() !== '' && r.fuel_unit.trim() !== 'L') err('fuel_unit', r.fuel_unit, 'Feil enhet: diesel må oppgis i «L»');
    let revision = 1;
    if (r.revision !== undefined && r.revision.trim() !== '') {
      const rv = Number(r.revision.trim());
      if (!Number.isInteger(rv) || rv < 1) err('revision', r.revision, 'Revisjon må være et heltall ≥ 1');
      else revision = rv;
    }
    const key = `${recordId}:${revision}`;
    if (recordId && seen.has(key)) err('record_id', recordId, `Duplikat av record_id + revisjon (også på rad ${seen.get(key)})`);
    seen.set(key, rowNo);
    rows.push({ row: rowNo, recordId, machineId, fromUtc, toUtc, engineHours: e.value, idleHours: i.value, fuelLitres: f.value, reportedCo2Kg: c.value, quality, revision, activity, sourceSystem });
  });
  return { ok: errors.length === 0, rows, errors, headerErrors, rowCount: data.length, delimiter: parsed.meta.delimiter };
}

export interface ImportSummary {
  added: number;
  replaced: number;
  unchanged: number;
  ignoredOlderRevision: number;
  overlapWarnings: string[];
}

/**
 * Idempotent upsert: identitet = sourceId + sourceRecordId + revision.
 * Lik revisjon → uendret. Høyere revisjon → erstatter gammelt bidrag (gammel versjon beholdes i historikk-feltet).
 */
export function applyImport(ds: Dataset, rows: ParsedRow[], ingestedAtUtc: string): { dataset: Dataset; summary: ImportSummary; history: MachineInterval[] } {
  const intervals = [...ds.machineIntervals];
  const history: MachineInterval[] = [];
  const summary: ImportSummary = { added: 0, replaced: 0, unchanged: 0, ignoredOlderRevision: 0, overlapWarnings: [] };
  const byRecordId = new Map<string, number>();
  intervals.forEach((mi, idx) => byRecordId.set(mi.sourceRecordId, idx));
  // Kortform uten «DEMO-» (slik kildepost-ID-er vises i portalen) skal treffe samme underliggende post.
  const byShortId = new Map<string, number>();
  intervals.forEach((mi, idx) => byShortId.set(mi.sourceRecordId.replace(/DEMO-/g, ''), idx));
  for (const row of rows) {
    let idx = byRecordId.get(row.recordId);
    let r = row;
    if (idx === undefined) {
      const alt = byShortId.get(row.recordId);
      if (alt !== undefined) {
        idx = alt;
        r = { ...row, recordId: intervals[alt].sourceRecordId };
      }
    }
    const next: MachineInterval = {
      id: `imp-${r.recordId}-r${r.revision}`,
      machineId: r.machineId,
      fromUtc: r.fromUtc,
      toUtc: r.toUtc,
      engineHours: r.engineHours,
      idleHours: r.idleHours,
      fuelLitres: r.fuelLitres,
      idleFuelLitres: null,
      reportedCo2Kg: r.reportedCo2Kg ?? null,
      quality: r.quality === 'measured' ? 'imported' : r.quality,
      sourceId: 'maskindata',
      sourceRecordId: r.recordId,
      revision: r.revision,
      scenarioId: ds.scenarioId,
      ingestedAtUtc,
    };
    if (idx === undefined) {
      const overlapping = intervals.filter((x) => x.machineId === r.machineId && x.fromUtc < r.toUtc && x.toUtc > r.fromUtc);
      if (overlapping.length) summary.overlapWarnings.push(`Rad ${r.row}: ${r.recordId} overlapper ${overlapping.length} eksisterende intervall for ${r.machineId} (${overlapping.map((o) => o.sourceRecordId).slice(0, 3).join(', ')}). Begge inngår i summene – kontroller om det er duplikat.`);
      intervals.push(next);
      byRecordId.set(r.recordId, intervals.length - 1);
      summary.added++;
    } else {
      const existing = intervals[idx];
      if (r.revision === existing.revision) {
        summary.unchanged++;
      } else if (r.revision > existing.revision) {
        history.push(existing);
        intervals[idx] = { ...next, id: existing.id };
        summary.replaced++;
      } else {
        summary.ignoredOlderRevision++;
      }
    }
  }
  return { dataset: { ...ds, machineIntervals: intervals }, summary, history };
}
